import { FieldArray, FormikProvider, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import * as Yup from "yup";
import RenderField from "./subComponents/RenderField";
import {
  useCreateProject,
  useGetContentCategoryList,
} from "../../../../../queries/query-hooks/workspace/workspace-hook";
import postService from "../../../../../api/services/post-service";
import {
  ADD_PROJECT,
  GET_CONTENT_SEARCH_GROUP_LIST,
  UPDATE_PROJECT,
} from "../../../../../api/endpoints/channel-endpoint";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { GET_PROJECT_POST_LIST } from "../../../../../queries/query-constants/workspace/workspace-constant";
import useRedirect from "../../../../../components/custom-hooks/use-redirect";
import useBackdrop from "../../../../../components/custom-hooks/use-backdrop";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import { getPrincipleEntitiesForSelect } from "../../../../../common/utils/utils";
import MappedStates from "./subComponents/MappedStates";
import MappedSpecialities from "./subComponents/MappedSpecialities";
import FormBuilder from "./subComponents/FormBuilder";
import { findIcon } from "views/dashboard/icons";

function SponsoredContentForm({
  project_type_code,
  project_group_id,
  project_type_id,
  projectDetails = [],
  masterSpecialities = [],
  stateLists = [],
}) {
  const { redirectTo } = useRedirect();
  const queryClient = useQueryClient();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const [selectedPermissions, setSelectedPermissions] = useState([
    "show_read",
    "show_view",
    "show_hcp",
    "show_CTA",
  ]);
  const [showPermissionError, setShowPermissionError] = useState(false);
  const [dropdownGroups, setDropdownGroups] = useState([
    { id: 1, firstSelect: "", secondSelect: "", isLocked: false },
  ]);
  const [specialitiesGroups, setSpecialitiesGroups] = useState([
    { id: 1, firstSelect: "", secondSelect: "", isLocked: false },
  ]);
  const [createdTags, setCreatedTags] = useState([]);
  const [isEditProject, setIsEditProject] = useState(false);

  useEffect(() => {
    if (projectDetails.length > 0) {
      setIsEditProject(true);
      preFillProjectForm();
    }
  }, []);

  const getMappedData = (stateData = []) => {
    const mapped_data = {};

    stateData.forEach((item) => {
      if (item?.firstSelect && item?.secondSelect) {
        mapped_data[item.firstSelect] = item.secondSelect;
      }
    });

    return mapped_data;
  };

  const formik = useFormik({
    initialValues: {
      showSelection: false,
      project_name: "",
      project_desc: "",
      principal_entity_id: "",
      project_start_date: "",
      project_end_date: "",
      project_contact_name: "",
      project_contact_email: "",
      project_contact_phone: "",
      project_po_ref: "",
      project_po_date: "",
      project_logo: "",
      project_group_id: project_group_id,
      ga_status: 0,
      project_type_id: project_type_id,
      project_impressions_target: 0,
      project_unique_reach_target: 0,
      project_reads_target: 0,
      project_status: 1,
      project_owner: "",
      project_to_content: [
        {
          label: "",
          content_id: "",
          content_category: "",
          view_target: 0,
          read_target: 0,
          unique_reach_target: 0,
        },
      ],
    },
    validationSchema: Yup.object({
      project_name: Yup.string().required("Project Name is required"),
      project_desc: Yup.string().required("Project Description is required"),
      principal_entity_id: Yup.string().required(
        "Principal Entity is required"
      ),
      project_start_date: Yup.date().required("Start date is required"),
      project_end_date: Yup.date().required("End date is required"),
      project_contact_name: Yup.string().required(
        "Project contact person name is required"
      ),
      project_contact_email: Yup.string()
        .email()
        .required("Project contact person Email is required"),
      project_contact_phone: Yup.string()
        .matches(/^[6-9]\d{9}$/, {
          message: "Please enter valid number.",
          excludeEmptyString: false,
        })
        .required("Project contact person phone is required"),
      project_po_ref: Yup.string().required("Project PO Reference is required"),
      project_po_date: Yup.string().required("Project PO Date is required"),
      project_logo: Yup.string().required("Project Logo is required"),
      ga_status: Yup.number().required("GA Status is required"),
      project_impressions_target: Yup.number()
        .required("Project Impressions Target is required")
        .moreThan(0, "Please enter valid Impression target."),
      project_unique_reach_target: Yup.number()
        .required("Project Impressions Target is required")
        .moreThan(0, "Please enter valid Impression target."),
      project_reads_target: Yup.number()
        .required("Project Reads Target is required")
        .moreThan(0, "Please enter valid Impression target."),
      project_owner: Yup.string()
        .trim()
        .required("Project Owner name is required"),
      project_to_content: Yup.array().of(
        Yup.object().shape({
          label: Yup.string()
            .trim()
            .required("Content Category Name is required"),

          content_id: Yup.string().trim().required("Please select a content"),

          view_target: Yup.number()
            .required("Content View Target is required")
            .moreThan(0, "Please enter valid Impression target."),

          read_target: Yup.number()
            .required("Content Read Target is required")
            .moreThan(0, "Please enter valid Reach target."),

          unique_reach_target: Yup.number()
            .required("Content Reach Target is required")
            .moreThan(0, "Please enter valid Reach target."),

          content_category: Yup.string()
            .trim()
            .required("Content Category is required"),
        })
      ),
    }),
    onSubmit: async (values) => {
      setShowPermissionError(false);
      showBackdrop();

      const payload = {
        ...values,
        project_attributes: [
          {
            key: "tags",
            value: JSON.stringify(createdTags),
          },
        ],
      };
      payload.project_status === 0
        ? (payload.project_status = 2)
        : (payload.project_status = 1);

      const mappedLocationData = getMappedData(dropdownGroups);
      const mappedSpecialityData = getMappedData(specialitiesGroups);

      selectedPermissions?.forEach((permission) => {
        payload.project_attributes.push({
          key: permission,
          value: "true",
        });
      });

      if (!isEmpty(mappedLocationData)) {
        payload.project_attributes.push({
          key: "mapped_location_data",
          value: JSON.stringify(mappedLocationData),
        });
      }
      if (!isEmpty(mappedSpecialityData)) {
        payload.project_attributes.push({
          key: "mapped_speciality_data",
          value: JSON.stringify(mappedSpecialityData),
        });
      }

      if (
        !selectedPermissions?.includes("show_view") &&
        !selectedPermissions?.includes("show_read") &&
        !selectedPermissions?.includes("show_hcp") &&
        !selectedPermissions?.includes("show_CTA")
      ) {
        setShowPermissionError(true);
        hideBackdrop();
        return;
      }
      delete payload?.dbRef;
      let response;
      if (projectDetails.length > 0) {
        response = await postService(UPDATE_PROJECT, payload);
      } else {
        response = await postService(ADD_PROJECT, payload);
      }

      if (!response.data?.success) {
        toast.error("Something went wrong");
        hideBackdrop();
      } else {
        queryClient.invalidateQueries({
          queryKey: [GET_PROJECT_POST_LIST],
        });

        if (projectDetails.length > 0) {
          toast.success("Project updated successfully");
        } else {
          toast.success("Project created successfully");
        }

        hideBackdrop();
        redirectTo("workspace/clirnet");
      }
    },
  });
  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
  } = formik;
  const { data: createProjectData, isLoading: principalEntitiesLoading } =
    useCreateProject();

  const onTagsChangeHandler = (selectedValue) => {
    setCreatedTags(selectedValue);
  };

  const tabOptions = [
    {
      item: "show_view",
      tab: "View",
      disabled: false,
    },
    {
      item: "show_read",
      tab: "Read",
      disabled: false,
    },
    {
      item: "show_hcp",
      tab: "HCP",
      disabled: false,
    },
    {
      item: "show_CTA",
      tab: "CTA",
      disabled: false,
    },
  ];

  const fields = [
    {
      row: 1,
      columns: [
        {
          id: 1,
          label: "Project Name",
          field_name: "project_name",
          type: "text",
          placeholder: "Enter Project Name",
          required: true,
          colSize: 6,
        },
        {
          id: 2,
          label: "Principal Entity",
          field_name: "principal_entity_id",
          type: "select",
          options: getPrincipleEntitiesForSelect(
            createProjectData?.principal_entities || []
          ),
          required: true,
          optionLabelKey: "principal_entity_name",
          optionValueKey: "principal_entity_id",
          colSize: 3,
        },
        {
          id: 3,
          label: "Project Status",
          field_name: "project_status",
          type: "switch",
          required: false,
          disabled: !isEditProject,
          colSize: 3,
        },
      ],
    },
    {
      row: 2,
      columns: [
        {
          id: 1,
          label: "Project Description",
          field_name: "project_desc",
          type: "textarea",
          placeholder: "Enter Project Description",
          required: true,
        },
        {
          id: 2,
          label: "Project Logo",
          field_name: "project_logo",
          type: "file",
          required: true,
        },
      ],
    },
    {
      row: 3,
      columns: [
        {
          id: 1,
          label: "Project Owner Name",
          field_name: "project_owner",
          type: "text",
          placeholder: "Enter Project Owner",
          required: true,
        },
        {
          id: 2,
          label: "Start Date",
          field_name: "project_start_date",
          type: "date",
          required: true,
        },
        {
          id: 3,
          label: "End Date",
          field_name: "project_end_date",
          type: "date",
          required: true,
        },
      ],
    },
    {
      row: 4,
      columns: [
        {
          id: 1,
          label: "Project Contant Person Name",
          field_name: "project_contact_name",
          type: "text",
          placeholder: "Enter Owner Contact",
          required: true,
        },
        {
          id: 2,
          label: "Project Contant Person Email",
          field_name: "project_contact_email",
          type: "email",
          placeholder: "Enter Owner Email",
          required: true,
        },
        {
          id: 3,
          label: "Project Contant Person Phone",
          field_name: "project_contact_phone",
          type: "number",
          placeholder: "Enter Owner Phone",
          required: true,
        },
      ],
    },
    {
      row: 5,
      columns: [
        {
          id: 1,
          label: "Purchase Order Reference",
          field_name: "project_po_ref",
          type: "text",
          placeholder: "Enter Purchase Order Reference",
          required: true,
        },
        {
          id: 2,
          label: "Purchase Order Date",
          field_name: "project_po_date",
          type: "date",
          placeholder: "Enter Purchase Order Date",
          required: true,
        },
      ],
    },
    {
      row: 6,
      columns: [
        {
          id: 1,
          label: "Project Impressions Target",
          field_name: "project_impressions_target",
          type: "number",
          required: true,
        },
        {
          id: 2,
          label: "Project Unique Reach Target",
          field_name: "project_unique_reach_target",
          type: "number",
          required: true,
        },
        {
          id: 3,
          label: "Project Reads Target",
          field_name: "project_reads_target",
          type: "number",
          required: true,
        },
      ],
    },
    {
      row: 7,
      columns: [
        {
          id: 1,
          label: "GA Status",
          field_name: "ga_status",
          type: "switch",
          required: false,
        },
        {
          id: 2,
          label: "Select Permissions",
          field_name: "select_permissions",
          type: "checkbox",
          required: true,
          options: tabOptions,
          selected: selectedPermissions,
          isCustomError: showPermissionError,
          customErrorMsg: "Please select atleast one permission (view/Read)",
          onCustomChange: (item) => {
            if (selectedPermissions?.includes(item)) {
              setSelectedPermissions(
                selectedPermissions?.filter((value) => value !== item)
              );
            } else {
              setSelectedPermissions((prev) => [...prev, item]);
            }
          },
        },
      ],
    },
    {
      row: 8,
      columns: [
        {
          id: 1,
          label: "Project Tags",
          field_name: "tags",
          type: "creatableSelect",
          required: false,
          options: createdTags,
          onCustomChange: onTagsChangeHandler,
        },
      ],
    },
  ];
  const [searchText, setSearchText] = useState({});

  const { data: contentCategories, isLoading: isContentCategoriesLoading } =
    useGetContentCategoryList();

  const _loadSuggestions = (
    inputValue,
    selectedCategory,
    content_category_id
  ) => {
    const searchValue = isNaN(inputValue) ? inputValue : Number(inputValue);
    return new Promise(async (resolve) => {
      const response = await postService(GET_CONTENT_SEARCH_GROUP_LIST, {
        slug: selectedCategory,
        search_key: searchValue,
        content_category_id: content_category_id,
      });

      if (!response.isError) {
        const searchData = [];
        response?.data?.data &&
          response?.data?.data.map((elem) => {
            elem.content_data.forEach((elem) => {
              searchData.push({
                value: elem.id,
                label: `${elem.content_old_id} - ${elem.title}`,
              });
            });
          });

        resolve(searchData);
      } else {
        toast.error("Error fetching data!");
      }
    });
  };

  function preFillProjectForm() {
    const projectImpressionsTarget =
      projectDetails?.at(0)?.project_target?.impressions_target || 0;
    const projectUniqueReachTarget =
      projectDetails?.at(0)?.project_target?.unique_reach_target || 0;
    const projectReadsTarget =
      projectDetails?.at(0)?.project_target?.project_reads_target || 0;
    const projectStatus = projectDetails?.at(0)?.project_status === 2 ? 0 : 1;

    const projectToContent = projectDetails
      ?.at(0)
      ?.project_contents?.map((elem) => {
        const content = elem.fetch_with_contents?.at(0);
        return {
          label: content?.content_title,
          content_id: content?.content_id,
          content_category: content?.content_category?.content_category_code,
          read_target: content?.content_target?.read_target,
          view_target: content?.content_target?.view_target,
          unique_reach_target: content?.content_target?.unique_reach_target,
        };
      });

    const projectPermissionAttribute = projectDetails
      ?.at(0)
      ?.project_attributes?.filter((item) => {
        return (
          item.attribute_name !== "mapped_location_data" &&
          item.attribute_name !== "mapped_speciality_data"
        );
      })
      .map((item) => {
        return item.attribute_name;
      });

    if (projectPermissionAttribute?.length > 0) {
      setSelectedPermissions(projectPermissionAttribute);
    }

    const stateMappedAttribute = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item.attribute_name === "mapped_location_data"
      )?.attribute_value;

    const specialityMappedAttribute = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) =>
          item?.attribute_name === "mapped_speciality_data" &&
          item?.attribute_value !== "true" &&
          item?.attribute_value !== "false"
      )?.attribute_value;

    const projectTags = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "tags"
      )?.attribute_value;

    const projectTagsArray = projectTags ? JSON.parse(projectTags) : [];

    if (!!stateMappedAttribute) {
      const mappedStatesData = JSON.parse(stateMappedAttribute);

      const transformedData = Object.keys(mappedStatesData).map(
        (key, index) => ({
          id: index + 1,
          firstSelect: key,
          secondSelect: mappedStatesData[key],
          isLocked: true,
        })
      );

      transformedData.push({
        id: transformedData.length + 1,
        firstSelect: "",
        secondSelect: "",
        isLocked: false,
      });

      setDropdownGroups(transformedData);
    }

    if (!!specialityMappedAttribute) {
      const mappedSpecialityData = JSON.parse(specialityMappedAttribute);

      const transformedData = Object.keys(mappedSpecialityData).map(
        (key, index) => ({
          id: index + 1,
          firstSelect: key,
          secondSelect: mappedSpecialityData[key],
          isLocked: true,
        })
      );

      transformedData.push({
        id: transformedData.length + 1,
        firstSelect: "",
        secondSelect: "",
        isLocked: false,
      });

      setSpecialitiesGroups(transformedData);
    }

    setCreatedTags(projectTagsArray);

    setValues({
      ...values,
      ...projectDetails?.at(0),
      project_status: projectStatus,
      project_impressions_target: projectImpressionsTarget,
      project_unique_reach_target: projectUniqueReachTarget,
      project_reads_target: projectReadsTarget,
      project_to_content: projectToContent || [],
    });
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormBuilder
        fields={fields}
        handleChange={handleChange}
        values={values}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
      />

      <div>
        <p className="form-label">Choose Contents</p>
        <FormikProvider value={formik}>
          <FieldArray name="project_to_content">
            {(prop) => (
              <>
                {values.project_to_content.map((item, index) => (
                  <div
                    key={index}
                    className="border border-1 border-primary border-opacity-25 border-dashed rounded-3 mb-4 p-4 px-3"
                  >
                    <Row>
                      <Col lg={6}>
                        <RenderField
                          type="select"
                          field_name={`project_to_content[${index}].content_category`}
                          isLoading={isContentCategoriesLoading}
                          values={values}
                          touched={touched}
                          errors={errors}
                          options={contentCategories?.map((item) => {
                            return {
                              label: item?.content_category_name,
                              value: item?.content_category_code,
                            };
                          })}
                          handleChange={(e) => {
                            handleChange(e);
                            setFieldValue(
                              `project_to_content[${index}].content_id`,
                              ""
                            );
                            setFieldValue(
                              `project_to_content[${index}].label`,
                              ""
                            );
                          }}
                          label={"Content Category"}
                          required={true}
                        />
                      </Col>

                      <Col lg={6}>
                        <RenderField
                          required={true}
                          type="searchable"
                          placeholder="Select Content"
                          label={`Select Content *`}
                          field_name={`project_to_content[${index}].content_id`}
                          searchText={searchText}
                          loadOptions={debounce((inputText, callback) => {
                            setSearchText(inputText);
                            _loadSuggestions(
                              inputText,
                              values?.project_to_content[index]
                                ?.content_category || "",
                              contentCategories?.find(
                                (item) =>
                                  item?.content_category_code ===
                                  values?.project_to_content[index]
                                    ?.content_category
                              )?.content_category_id
                            )
                              .then((options) => {
                                callback(options);
                              })
                              .catch((err) => console.log(err));
                          }, 300)}
                          handleChange={(val) => {
                            if (val) {
                              setFieldValue(
                                `project_to_content[${index}].content_id`,
                                val?.value || ""
                              );
                              setFieldValue(
                                `project_to_content[${index}].label`,
                                val?.label || ""
                              );
                            } else {
                              setFieldValue(
                                `project_to_content[${index}].content_id`,
                                ""
                              );
                              setFieldValue(
                                `project_to_content[${index}].label`,
                                ""
                              );
                            }
                          }}
                          selected={
                            values.project_to_content[index]?.label
                              ? {
                                  value:
                                    values.project_to_content[index]
                                      ?.content_id,
                                  label:
                                    values.project_to_content[index]?.label,
                                }
                              : ""
                          }
                          values={values}
                          touched={touched}
                          errors={errors}
                          setFieldValue={setFieldValue}
                          disabled={
                            values[`project_to_content`][index]
                              ?.content_category == ""
                              ? true
                              : false
                          }
                        />
                      </Col>

                      <Col lg={4}>
                        <RenderField
                          type="number"
                          field_name={`project_to_content[${index}].read_target`}
                          placeholder="Read Target"
                          values={values}
                          touched={touched}
                          errors={errors}
                          handleChange={handleChange}
                          label={"Read Target"}
                          required={true}
                        />
                      </Col>
                      <Col lg={4}>
                        <RenderField
                          type="number"
                          field_name={`project_to_content[${index}].view_target`}
                          placeholder="View Target"
                          values={values}
                          touched={touched}
                          errors={errors}
                          handleChange={handleChange}
                          label={"View Target"}
                          required={true}
                        />
                      </Col>
                      <Col lg={4}>
                        <RenderField
                          type="number"
                          field_name={`project_to_content[${index}].unique_reach_target`}
                          placeholder="Reach Target"
                          values={values}
                          touched={touched}
                          errors={errors}
                          handleChange={handleChange}
                          label={"Reach Target"}
                          required={true}
                        />
                      </Col>

                      <Col
                        lg={12}
                        className="d-flex justify-content-center align-items-center gap-2 flex-wrap"
                      >
                        <Button
                          variant="outline-danger"
                          className="border-dashed flex-shrink-0 flex-sm-grow-0 flex-grow-1 px-md-4 px-3"
                          onClick={() => prop?.remove(index)}
                          disabled={
                            index === 0 &&
                            values[`project_to_content`]?.length < 2
                              ? true
                              : false
                          }
                        >
                          <span>{findIcon("Trash", "dual-tone", 22)}</span>{" "}
                          Remove
                        </Button>

                        {values[`project_to_content`]?.length - 1 === index && (
                          <Button
                            variant={`${
                              values[`project_to_content`][index]?.label == ""
                                ? "outline-secondary"
                                : "outline-primary"
                            }`}
                            className="border-dashed flex-shrink-0 flex-sm-grow-0 flex-grow-1 px-4 px-3"
                            onClick={() =>
                              prop?.push({
                                label: "",
                                content_id: "",
                                view_target: 0,
                                read_target: 0,
                                unique_reach_target: 0,
                                content_category: "",
                              })
                            }
                            disabled={
                              values[`project_to_content`][index]?.label == ""
                                ? true
                                : false
                            }
                          >
                            <span>{findIcon("Plus", "solid", 22)}</span> Add Row
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </div>
                ))}
              </>
            )}
          </FieldArray>
        </FormikProvider>
      </div>

      <div>
        <p className="form-label">Mapped States</p>
        <MappedStates
          stateLists={stateLists}
          dropdownGroups={dropdownGroups}
          setDropdownGroups={setDropdownGroups}
        />
      </div>

      <div>
        <p className="form-label">Mapped Specialities</p>
        <MappedSpecialities
          masterSpecialities={masterSpecialities}
          specialitiesGroups={specialitiesGroups}
          setSpecialitiesGroups={setSpecialitiesGroups}
        />
      </div>

      <div className="d-flex justify-content-end gap-3">
        <Button
          variant="secondary"
          onClick={() => redirectTo("workspace/clirnet")}
        >
          Cancel
        </Button>{" "}
        <Button type="submit">{`${
          projectDetails.length > 0 ? "Update" : "Submit"
        }`}</Button>
      </div>
    </Form>
  );
}

export default SponsoredContentForm;
